import './style.scss';

import { useEffect, useMemo, useState } from 'react';

import { useObserverData } from '../../../hooks/useObserverData';
import type { IChecklistStepTemplate, IObserverChecklist } from '../../../interface/observer-checklist';
import { getChecklistTemplate, getMyChecklists, recordChecklistStep } from '../../../lib/vesr-api';
import { TypeButton } from '../../utils/button';
import { fromTimeInput, nextStepKey, outcomeMessage, recordedCount, shortTime, templateMessage, toTimeInput } from './model';

type Answer = boolean | undefined;

/** One step's editor: when it happened, the yes/no checks, and a note. */
const StepEditor = ({
	step,
	checklist,
	onSave,
}: {
	step: IChecklistStepTemplate;
	checklist: IObserverChecklist | null;
	onSave: (time: string, checks: Record<string, boolean>, note: string) => Promise<string | null>;
}) => {
	const existing = checklist?.steps?.[step.key];
	const [time, setTime] = useState(existing ? shortTime(existing.observedAt) : toTimeInput(new Date()));
	const [answers, setAnswers] = useState<Record<string, Answer>>(existing?.checks ?? {});
	const [note, setNote] = useState(existing?.note ?? '');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const save = async () => {
		setSaving(true);
		setError(null);
		const checks = Object.fromEntries(Object.entries(answers).filter(([, v]) => v !== undefined)) as Record<string, boolean>;
		const message = await onSave(time, checks, note);
		setSaving(false);
		setError(message);
	};

	return (
		<div className='oc-editor'>
			<label className='oc-editor__time'>
				<span>Time it happened</span>
				<input
					type='time'
					value={time}
					onChange={(e) => setTime(e.target.value)}
				/>
			</label>

			{step.checks.map((check) => (
				<fieldset
					key={check.key}
					className='oc-check'
				>
					<legend>{check.question}</legend>
					<div className='oc-check__choices'>
						{[
							{ label: 'Yes', value: true },
							{ label: 'No', value: false },
						].map((choice) => (
							<label
								key={choice.label}
								className={`oc-choice${answers[check.key] === choice.value ? ' oc-choice--on' : ''}`}
							>
								<input
									type='radio'
									name={`${step.key}-${check.key}`}
									checked={answers[check.key] === choice.value}
									onChange={() => setAnswers((a) => ({ ...a, [check.key]: choice.value }))}
								/>
								{choice.label}
							</label>
						))}
					</div>
				</fieldset>
			))}

			<label className='oc-editor__note'>
				<span>Note (optional)</span>
				<textarea
					value={note}
					maxLength={1000}
					rows={3}
					onChange={(e) => setNote(e.target.value)}
					placeholder='What you saw, in your own words'
				/>
			</label>

			{error ? (
				<p
					className='oc-editor__error'
					role='alert'
				>
					{error}
				</p>
			) : null}

			<TypeButton
				title={existing ? 'Update step' : 'Save step'}
				buttonSize='small'
				buttonType='bold'
				className='w-100'
				load={saving}
				onClick={() => void save()}
			/>
		</div>
	);
};

/**
 * An independent observer's election-day checklist for the unit they are posted to. The steps are
 * the real election-day sequence, so they are numbered; the next unrecorded step opens first.
 */
const ObserverChecklist = () => {
	const observer = useObserverData();
	const posting = observer.postings[0];
	const [steps, setSteps] = useState<IChecklistStepTemplate[]>([]);
	const [checklist, setChecklist] = useState<IObserverChecklist | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [open, setOpen] = useState<string | null>(null);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		if (!posting) return;
		let live = true;
		void (async () => {
			const [template, mine] = await Promise.all([getChecklistTemplate(), getMyChecklists(posting.electionId)]);
			if (!live) return;
			const templateBody = template.reachable ? template.body?.data : undefined;
			if (!templateBody) {
				setLoadError(templateMessage(template) ?? 'The checklist could not be loaded. Try again.');
				setLoaded(true);
				return;
			}
			const own = (mine.reachable ? mine.body?.data : undefined)?.find((c) => c.entityCode === posting.entityCode) ?? null;
			setSteps(templateBody);
			setChecklist(own);
			setOpen(nextStepKey(templateBody, own));
			setLoaded(true);
		})();
		return () => {
			live = false;
		};
	}, [posting?.electionId, posting?.entityCode]);

	const done = useMemo(() => recordedCount(steps, checklist), [steps, checklist]);

	if (!posting) {
		return (
			<p className='oc-empty'>
				{observer.status === 'loading'
					? 'Loading your posting…'
					: 'You can keep a checklist once your observer desk assigns you a polling unit.'}
			</p>
		);
	}

	const save = async (step: IChecklistStepTemplate, time: string, checks: Record<string, boolean>, note: string) => {
		const outcome = await recordChecklistStep(posting.electionId, posting.entityCode, step.key, {
			observedAt: fromTimeInput(time),
			checks,
			note: note.trim() || undefined,
		});
		const message = outcomeMessage(outcome);
		if (!message && outcome.reachable && outcome.body?.data) {
			const next = outcome.body.data;
			setChecklist(next);
			setOpen(nextStepKey(steps, next));
		}
		return message;
	};

	return (
		<div className='oc'>
			<header className='oc__header'>
				<p className='oc__place'>{posting.pollingUnitName || posting.wardName || posting.entityCode}</p>
				<p className='oc__progress'>{loaded ? `${done} of ${steps.length} steps recorded` : 'Loading checklist…'}</p>
			</header>

			{loadError ? (
				<p
					className='oc-editor__error'
					role='alert'
				>
					{loadError}
				</p>
			) : null}

			<ol className='oc-steps'>
				{steps.map((step, i) => {
					const record = checklist?.steps?.[step.key];
					const isOpen = open === step.key;
					return (
						<li
							key={step.key}
							className={`oc-step${record ? ' oc-step--done' : ''}${isOpen ? ' oc-step--open' : ''}`}
						>
							<button
								type='button'
								className='oc-step__head'
								aria-expanded={isOpen}
								onClick={() => setOpen(isOpen ? null : step.key)}
							>
								<span
									className='oc-step__marker'
									aria-hidden='true'
								>
									{i + 1}
								</span>
								<span className='oc-step__title'>{step.title}</span>
								<span className='oc-step__status'>{record ? shortTime(record.observedAt) : 'Not yet'}</span>
							</button>
							{isOpen ? (
								<StepEditor
									key={`${step.key}:${record?.recordedAt ?? 'new'}`}
									step={step}
									checklist={checklist}
									onSave={(time, checks, note) => save(step, time, checks, note)}
								/>
							) : null}
						</li>
					);
				})}
			</ol>
		</div>
	);
};

export default ObserverChecklist;
